import { call, put, select, takeLatest } from 'redux-saga/effects';
import {
  deleteCatalogCollection as deleteCatalogCollectionAPI,
  getCatalogCollections as getCatalogCollectionsAPI,
} from 'api/CatalogAPI';
import { addToRootSaga } from 'store';
import { defaultQueryParams, formatSortAndOrder } from 'core/table';
import { addPositiveToast, addNegativeToastWithErrorId } from 'modules/toastContainer';
import { UPDATE_COLLECTION_RESPONSE } from 'modules/catalog/collectionBuilder/actionTypes';
import * as actions from './actions';
import * as actionTypes from './actionTypes';
import { collectionsSelector } from './selectors';
import { errorIds } from '../errorIds';

/**
 * deleteCatalogCollection
 * Deletes a catalog collection.
 * @returns { Object } Fetched catalog collections.
 */
export function* deleteCatalogCollection() {
  try {
    const { collections, selectedCollection } = yield select(collectionsSelector);
    const { name, id } = selectedCollection;
    const successMessage = `Successfully deleted ${name}.`;
    const rows = collections.filter((collection) => collection.id !== id);
    yield call(deleteCatalogCollectionAPI, id);
    yield put(actions.deleteCatalogCollectionResponse(rows, rows.length));
    yield put(actions.closeDeleteModal());
    yield put(addPositiveToast(successMessage));
  } catch (error) {
    yield put(actions.deleteCatalogCollectionError(error));
    yield put(
      addNegativeToastWithErrorId(
        'Error deleting collection.',
        errorIds.catalogCollection.delete,
        error,
      ),
    );
  }
}

/**
 * fetchCatalogCollections
 * Fetches catalog collections.
 * @returns { Object } Fetched catalog collections.
 */
export function* fetchCatalogCollections() {
  try {
    const { fetchParams } = yield select(collectionsSelector);
    const params = formatSortAndOrder({ ...defaultQueryParams, ...fetchParams });
    const response = yield call(getCatalogCollectionsAPI, params);
    yield put(actions.fetchCatalogCollectionsResponse(response.data[0], response.data[1]));
  } catch (error) {
    yield put(actions.fetchCatalogCollectionsError(error));
  }
}

/**
 * fetchRecentCatalogCollections
 * Fetches recently updated catalog collections.
 * @returns { Object } Fetched catalog collections.
 */
export function* fetchRecentCatalogCollections() {
  try {
    const params = formatSortAndOrder({
      ...defaultQueryParams,
      pageSize: 3,
      orderBy: 'updatedAt',
      sorting: 'desc',
    });
    const response = yield call(getCatalogCollectionsAPI, params);
    yield put(actions.fetchCatalogCollectionsResponse(response.data, response.data.length));
  } catch (error) {
    yield put(actions.fetchCatalogCollectionsError(error));
  }
}

addToRootSaga([
  takeLatest(
    [actionTypes.FETCH_CATALOG_COLLECTIONS, UPDATE_COLLECTION_RESPONSE],
    fetchCatalogCollections,
  ),
  takeLatest(actionTypes.DELETE_CATALOG_COLLECTION, deleteCatalogCollection),
  takeLatest(actionTypes.FETCH_RECENT_CATALOG_COLLECTIONS, fetchRecentCatalogCollections),
]);
